import { useMemo } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { Table } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import NotificationRulesTableFacedFilter from './notification-rules-table-faceted-filter.tsx';
import {
  NotificationRule,
  notificationRuleStatuses,
} from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { CreateNotificationRule } from '@/features/notification-rules/components/create-notification-rule/create-notification-rule.tsx';
import { Cross2Icon } from '@radix-ui/react-icons';
import { NotificationRulesTableToolbarDeleteDialog } from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-toolbar-delete-dialog.tsx';
import { NotificationRulesTableToolbarEditDropdown } from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-toolbar-edit-dropdown-menu.tsx';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { DEVICE_TYPES_NAMES_ARRAY } from '@/features/devices/schemas/devices.schemas.ts';
import {
  getLabelPlantStatus,
  getCompactPlantStatus,
} from '@/features/plants/schemas/plants.utils.ts';

const ruleTypeOptions = [
  { value: 'plant', label: 'Plant' },
  { value: 'device', label: 'Device' },
  { value: 'schedule', label: 'Schedule' },
];

const deviceTypeOptions = DEVICE_TYPES_NAMES_ARRAY.map((name) => ({
  value: name,
  label: name,
}));

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function NotificationRulesTableToolbar<TData extends NotificationRule>({
  table,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const isMobile = useMediaQuery('(max-width:640px)');
  const { data: plants } = useGetPlantsQuery({ includeOverview: false });

  const plantOptions = useMemo(
    () =>
      (plants ?? []).map((plant) => ({
        value: plant.basicDetails.name,
        label: plant.basicDetails.name,
        badge:
          plant.basicDetails.status !== 'Operational'
            ? {
                label: getCompactPlantStatus(plant.basicDetails.status, isMobile),
                variant: getLabelPlantStatus(plant.basicDetails.status),
              }
            : undefined,
      })),
    [plants, isMobile]
  );

  return (
    <div className="flex justify-between">
      <div className="flex flex-1 flex-col flex-wrap gap-2 sm:flex-row">
        <div className="flex justify-between gap-2">
          <Input
            placeholder="Filter notification rules by title..."
            value={(table.getColumn('notificationTitle')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('notificationTitle')?.setFilterValue(event.target.value)
            }
            className="h-8 w-full max-w-[300px] bg-white"
          />
          <div className="block sm:hidden">
            <CreateNotificationRule />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {table.getColumn('status') && (
            <NotificationRulesTableFacedFilter
              column={table.getColumn('status')}
              title="Status"
              options={notificationRuleStatuses}
            />
          )}
          {table.getColumn('plantName') && (
            <NotificationRulesTableFacedFilter
              column={table.getColumn('plantName')}
              title="Plant"
              options={plantOptions}
            />
          )}
          {table.getColumn('ruleType') && (
            <NotificationRulesTableFacedFilter
              column={table.getColumn('ruleType')}
              title="Rule Type"
              options={ruleTypeOptions}
            />
          )}
          {table.getColumn('deviceType') && (
            <NotificationRulesTableFacedFilter
              column={table.getColumn('deviceType')}
              title="Device Type"
              options={deviceTypeOptions}
            />
          )}
          {isFiltered && (
            <Button
              variant="ghost"
              onClick={() => table.resetColumnFilters()}
              className="h-8 w-min px-2 lg:px-3"
            >
              Clear
              <Cross2Icon className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="order-2 flex flex-wrap gap-2 sm:order-0">
          {table.getFilteredSelectedRowModel().rows.length > 0 ? (
            <NotificationRulesTableToolbarEditDropdown
              notificationRules={table
                .getFilteredSelectedRowModel()
                .rows.map((row) => row.original)}
              onSuccess={() => table.toggleAllRowsSelected(false)}
            />
          ) : null}
          {table.getFilteredSelectedRowModel().rows.length > 0 ? (
            <NotificationRulesTableToolbarDeleteDialog
              notificationRules={table
                .getFilteredSelectedRowModel()
                .rows.map((row) => row.original)}
              onSuccess={() => table.toggleAllRowsSelected(false)}
            />
          ) : null}
        </div>
        {/*<NotificationRulesTableViewOptions table={table} />*/}
        <div className="hidden sm:block">
          <CreateNotificationRule />
        </div>
      </div>
    </div>
  );
}
